import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import {
  subscribeLiveConnections,
  getLiveConnection,
  connectWebSocket,
  disconnectLive,
  sendLive,
  clearLiveLog,
} from './liveConnections';

/**
 * @typedef {'idle'|'connecting'|'connected'|'disconnected'|'error'} WsStatus
 * @typedef {{ id: string, direction: 'in'|'out', data: string, timestamp: number }} WsMessage
 */

/**
 * A view onto the WebSocket of ONE tab. The connection and its log live in
 * `liveConnections` — not in this component — so leaving the tab and coming back finds
 * the socket still connected and the log intact. It ends when the tab closes.
 *
 * @param {{ tabId: string|number, onStatusChange?: (status: WsStatus) => void }} opts
 *   `onStatusChange` fires for changes seen WHILE MOUNTED — not for the status a tab
 *   already had when it was opened again (no "connected" toast on every tab switch).
 */
export function useWebSocket(opts = {}) {
  const { tabId } = opts;
  const snapshot = useSyncExternalStore(
    subscribeLiveConnections,
    useCallback(() => getLiveConnection(tabId), [tabId]),
  );

  const onStatusChangeRef = useRef(opts.onStatusChange);
  onStatusChangeRef.current = opts.onStatusChange;
  const seen = useRef({ tabId, status: snapshot.status });
  useEffect(() => {
    const before = seen.current;
    seen.current = { tabId, status: snapshot.status };
    if (before.tabId !== tabId) return; // another tab's status is not a CHANGE
    if (before.status !== snapshot.status) onStatusChangeRef.current?.(snapshot.status);
  }, [tabId, snapshot.status]);

  const connect = useCallback((url, config) => connectWebSocket(tabId, url, config), [tabId]);
  const disconnect = useCallback(() => disconnectLive(tabId), [tabId]);
  const sendMessage = useCallback((text, shownText) => { sendLive(tabId, text, shownText); }, [tabId]);
  const clearMessages = useCallback(() => clearLiveLog(tabId), [tabId]);

  return { status: snapshot.status, messages: snapshot.messages, dropped: snapshot.dropped, connect, disconnect, sendMessage, clearMessages };
}
